import React, {memo} from 'react';
import PropTypes from 'prop-types';

import {EdapProvider} from '@homeaway/react-event-tracking';

// EdapProvider.whyDidYouRender = true;

import {SearchFormContextProvider} from '../../contexts/SearchFormContext';

import {PAGE_TYPES} from '../../utils/pages';
import {FIELD_AUTO_PROGRESS_TYPES} from '../../utils/field';
import {SUBMIT_ACTION_TYPES} from '../../utils/submit';
import {MODAL_ANIMATION_TYPES} from '../../utils/modal';

import {SearchForm} from '../SearchForm/SearchForm';

import {useComponent} from './hooks/useComponent';

const SearchFormContainer = memo(function SearchFormContainer(props) {
    const {
        edapProviderFields,
        edapProviderOptions,
        edapProviderEventFields,
        edapProviderEventOptions,
        searchFormContextProviderValue
    } = useComponent(props);

    const dataForSearchFormPresentedEvent = {
        datesValues: props.datesValues,
        destinationValues: props.destinationValues,
        guestsValues: props.guestsValues,

    };

    return (
        <SearchFormContextProvider value={searchFormContextProviderValue}>
            <EdapProvider
                fields={edapProviderFields}
                options={edapProviderOptions}
                eventFields={edapProviderEventFields}
                eventOptions={edapProviderEventOptions}
            >
                <SearchForm dataForSearchFormPresentedEvent={dataForSearchFormPresentedEvent}/>
            </EdapProvider>
        </SearchFormContextProvider>
    );
});

SearchFormContainer.propTypes = {
    systemOptions: PropTypes.shape({
        site: PropTypes.string,
        baseUrl: PropTypes.string,
        appPrefix: PropTypes.string,
        pageType: PropTypes.oneOf(PAGE_TYPES),
        locale: PropTypes.string
    }),
    mediaEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func
    }),
    mediaOptions: PropTypes.shape({
        smallBreakpoint: PropTypes.number,
        largeBreakpoint: PropTypes.number
    }),
    mediaSettings: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        isMobile: PropTypes.bool,
        isTablet: PropTypes.bool,
        isDesktop: PropTypes.bool
    }),
    abacusStore: PropTypes.shape({
        experimentData: PropTypes.objectOf(PropTypes.shape({
            entityId: PropTypes.string,
            experimentKey: PropTypes.shape({
                experimentId: PropTypes.number,
                experimentName: PropTypes.string
            }),
            experimentInstanceId: PropTypes.number,
            value: PropTypes.number
        })),
        experimentNames: PropTypes.arrayOf(PropTypes.string)
    }),
    edapOptions: PropTypes.shape({
        searchSubmitEventActionLocation: PropTypes.string,
        searchSubmitEventAppData: PropTypes.oneOfType[PropTypes.object],
        searchQuerySubmittedEventActionLocation: PropTypes.string,
        destinationSummaryClickEventAction: PropTypes.string,
        destinationSummaryClickEventCategory: PropTypes.string,
        submitSummaryClickEventAction: PropTypes.string,
        submitSummaryClickEventCategory: PropTypes.string,
        detailsModalCloseEventAction: PropTypes.string,
        detailsModalCloseEventCategory: PropTypes.string
    }),
    searchEvents: PropTypes.shape({
        onCurrentValuesChange: PropTypes.func,
        onLatestValuesChange: PropTypes.func
    }),
    searchValues: PropTypes.shape({
        filters: PropTypes.arrayOf(PropTypes.number)
    }),
    formEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func,
        onSubmit: PropTypes.func
    }),
    formOptions: PropTypes.shape({
        allowErrorHighlighting: PropTypes.bool,
        allowSummary: PropTypes.bool,
        autoProgress: PropTypes.oneOf(FIELD_AUTO_PROGRESS_TYPES),
        autoSubmit: PropTypes.bool,
        layout: PropTypes.oneOf(['standard', 'feature', 'advance', 'error-state']),
        align: PropTypes.oneOf(['center', 'left', 'right']),
        showIcons: PropTypes.bool,
        showGuestsDetailsInputField: PropTypes.bool,
        showSubmitDetailsButtonField: PropTypes.bool,
    }),
    formSettings: PropTypes.shape({}),
    destinationEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func,
        onCurrentValuesChange: PropTypes.func,
        onLatestValuesChange: PropTypes.func,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        onMount: PropTypes.func,
        onSearchSelected: PropTypes.func
    }),
    destinationOptions: PropTypes.shape({
        allowErrorHighlighting: PropTypes.bool,
        autoProgress: PropTypes.oneOf(FIELD_AUTO_PROGRESS_TYPES),
        filterType: PropTypes.oneOf([0, 1, 2, 3]),
        googlePlaceApiKey: PropTypes.string,
        googlePlaceApiUrl: PropTypes.string,
        hideSelectedFilter: PropTypes.bool,
        highlightMode: PropTypes.oneOf(['match', 'non-match']),
        iconName: PropTypes.string,
        id: PropTypes.string,
        matchMode: PropTypes.oneOf(['contiguous', 'non-contiguous']),
        minLength: PropTypes.number,
        popoverDesktopMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        popoverDesktopMinMarginRight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        popoverDesktopMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        popoverDesktopWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        popoverMaxDisplayCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        popoverMaxDisplayCountMobile: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        recentSearchesDisplayMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        remotePath: PropTypes.string,
        retainUserInput: PropTypes.bool,
        showIcon: PropTypes.bool,
        showInlineSuggestion: PropTypes.bool,
        showRecentSearches: PropTypes.bool,
        showResultIcons: PropTypes.bool,
        showResultLabels: PropTypes.bool,
        showSpinner: PropTypes.bool,
        showTrendingSuggestions: PropTypes.bool,
        truncateResultTerms: PropTypes.bool,
        usePopoverAnimation: PropTypes.bool,
        useResultsAnimation: PropTypes.bool
    }),
    destinationSettings: PropTypes.shape({
        isTypeaheadInputFocus: PropTypes.bool
    }),
    destinationValues: PropTypes.shape({
        destination: PropTypes.string,
        destinationUuid: PropTypes.string,
        subDestination: PropTypes.shape({
            filter: PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string
            })
        }),
        listingProps: PropTypes.shape({
            listingId: PropTypes.string,
            listingUrl: PropTypes.string,
            unitId: PropTypes.string
        })
    }),
    destinationSummaryEvents: PropTypes.shape({
        onClick: PropTypes.func
    }),
    datesEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func,
        onCurrentValuesChange: PropTypes.func,
        onLatestValuesChange: PropTypes.func,
        onCancel: PropTypes.func,
        onClose: PropTypes.func,
        onDateRangeChange: PropTypes.func,
        onDateRangeClear: PropTypes.func,
        onDisabledDayClick: PropTypes.func,
        onFocus: PropTypes.func,
        onOpen: PropTypes.func,
        onSelectionModeChange: PropTypes.func,
        onSubmit: PropTypes.func
    }),
    datesOptions: PropTypes.shape({
        actionLocation: PropTypes.string,
        alertTemplateComponent: PropTypes.func,
        alertTemplateData: PropTypes.object,
        allowErrorHighlighting: PropTypes.bool,
        autoProgress: PropTypes.oneOf(FIELD_AUTO_PROGRESS_TYPES),
        centered: PropTypes.bool,
        consolidateInputs: PropTypes.bool,
        dateFormatOptions: PropTypes.object,
        dayTemplateComponent: PropTypes.func,
        disableTextInput: PropTypes.bool,
        displayYearFirst: PropTypes.bool,
        enableSameDayDateRange: PropTypes.bool,
        fadeOut: PropTypes.bool,
        fadeOutDuration: PropTypes.number,
        id: PropTypes.string,
        inputIcon1Name: PropTypes.string,
        inputIcon2Name: PropTypes.string,
        inputName1: PropTypes.string,
        inputName2: PropTypes.string,
        maxDate: PropTypes.instanceOf(Date),
        minDate: PropTypes.instanceOf(Date),
        parentNode: PropTypes.any,
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        positionIconRight: PropTypes.bool,
        scrollNode: PropTypes.any,
        showArrow: PropTypes.bool,
        showInputIcon1: PropTypes.bool,
        showInputIcon2: PropTypes.bool,
        splitInputs: PropTypes.bool,
        wdioPrefix: PropTypes.string
    }),
    datesSettings: PropTypes.shape({
        dayData: PropTypes.object,
        startDayDataId: PropTypes.string,
        endDayDataId: PropTypes.string,
        selectedDayDataId: PropTypes.string,
        isOpen: PropTypes.bool,
        readOnly: PropTypes.bool
    }),
    datesValues: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string
    }),
    guestsEvents: PropTypes.shape({
        onAgeChange: PropTypes.func,
        onSettingsChange: PropTypes.func,
        onCurrentValuesChange: PropTypes.func,
        onLatestValuesChange: PropTypes.func,
        onChange: PropTypes.func,
        onClear: PropTypes.func,
        onClose: PropTypes.func,
        onExit: PropTypes.func,
        onOpen: PropTypes.func
    }),
    guestsOptions: PropTypes.shape({
        actionLocation: PropTypes.string,
        adultsMax: PropTypes.number,
        adultsMin: PropTypes.number,
        ageOptions: PropTypes.shape({
            minVal: PropTypes.number,
            maxVal: PropTypes.number,
            minAge: PropTypes.number,
            undefAgesValue: PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
                disabled: PropTypes.bool,
            }),
            useUndefAges: PropTypes.bool,
        }),
        allowErrorHighlighting: PropTypes.bool,
        autoSubmit: PropTypes.bool,
        centered: PropTypes.bool,
        childrenMax: PropTypes.number,
        childrenMin: PropTypes.number,
        disableTextInput: PropTypes.bool,
        fadeOut: PropTypes.bool,
        fadeOutDuration: PropTypes.number,
        id: PropTypes.string,
        inputIconName: PropTypes.string,
        isAriaRoleExperimentEnabled: PropTypes.bool,
        name: PropTypes.string,
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        positionIconRight: PropTypes.bool,
        showAgeRanges: PropTypes.bool,
        showAgeSelect: PropTypes.bool,
        showClearButton: PropTypes.bool,
        showContinue: PropTypes.bool,
        showInputIcon: PropTypes.bool,
        showPetOption: PropTypes.bool,
        wdioPrefix: PropTypes.string
    }),
    guestsSettings: PropTypes.shape({
        disableButton: PropTypes.bool,
        isOpen: PropTypes.bool,
        readOnly: PropTypes.bool
    }),
    guestsValues: PropTypes.shape({
        adultsCount: PropTypes.number,
        ageData: PropTypes.array,
        childrenCount: PropTypes.number,
        petIncluded: PropTypes.bool
    }),
    submitEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func,
        onClick: PropTypes.func
    }),
    submitOptions: PropTypes.shape({
        actionType: PropTypes.oneOf(SUBMIT_ACTION_TYPES),
        iconName: PropTypes.string,
        isPrimaryAction: PropTypes.bool,
        showIcon: PropTypes.bool
    }),
    submitSummaryEvents: PropTypes.shape({
        onClick: PropTypes.func
    }),
    submitDetailsEvents: PropTypes.shape({
        onClick: PropTypes.func
    }),
    detailsModalEvents: PropTypes.shape({
        onSettingsChange: PropTypes.func,
        onClose: PropTypes.func,
        onOpen: PropTypes.func,
        onSecondaryActionClick: PropTypes.func
    }),
    detailsModalOptions: PropTypes.shape({
        id: PropTypes.string,
        fixedBody: PropTypes.bool,
        parentNode: PropTypes.object,
        portalClassName: PropTypes.string,
        isContextual: PropTypes.bool,
        isInstructional: PropTypes.bool,
        isWorkflow: PropTypes.bool,
        title: PropTypes.element,
        footer: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.arrayOf(PropTypes.element)
        ]),
        showBackdrop: PropTypes.bool,
        showHeader: PropTypes.bool,
        showCloseButton: PropTypes.bool,
        closeOnEsc: PropTypes.bool,
        closeOnOutsideClick: PropTypes.bool,
        animation: PropTypes.oneOf(MODAL_ANIMATION_TYPES),
        timeouts: PropTypes.shape({
            backdrop: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            }),
            modal: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            })
        }),
        scrollTop: PropTypes.bool,
        scrollToTop: PropTypes.func
    }),
    detailsModalSettings: PropTypes.shape({
        isOpen: PropTypes.bool,
        isLoading: PropTypes.bool,
        isSecondaryActionDisabled: PropTypes.bool
    }),
    translations: PropTypes.object
};

SearchFormContainer.defaultProps = {
    systemOptions: {},
    mediaEvents: {},
    mediaOptions: {},
    mediaSettings: {},
    abacusStore: {},
    edapOptions: {},
    searchEvents: {},
    searchValues: {},
    formEvents: {},
    formOptions: {},
    formSettings: {},
    destinationEvents: {},
    destinationOptions: {},
    destinationSettings: {},
    destinationValues: {},
    destinationSummaryEvents: {},
    datesEvents: {},
    datesOptions: {},
    datesSettings: {},
    datesValues: {},
    guestsEvents: {},
    guestsOptions: {},
    guestsSettings: {},
    guestsValues: {},
    submitEvents: {},
    submitOptions: {},
    submitSummaryEvents: {},
    submitDetailsEvents: {},
    detailsModalEvents: {},
    detailsModalOptions: {},
    detailsModalSettings: {},
    translations: {}
};

SearchFormContainer.whyDidYouRender = true;

export default SearchFormContainer;
export {SearchFormContainer as SearchForm};
